import { usePermissions } from 'react-admin';
import { Dashboard } from '../dashboard/Dashboard';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { hashQueryParams } from '../../shared/hashQuery';
import { PlannerDashboard } from './FtlDashboards';
import { FEATURE_PLANNER_DASHBOARD, ROLE_PLANNER_FTL } from './ftlDashboardsApi';
import { IntakeDashboard } from '../intake/IntakeDashboard';
import { FEATURE_INTAKE_DEPARTMENTS } from '../intake/intakeSides';

/** Role działów przyjęcia zleceń — zaczynają od pulpitu przyjęcia (fala B). */
const INTAKE_START_ROLES: readonly string[] = [
  'CS',
  'ADMINISTRATION',
  // Team Leaderzy działów przyjęcia zleceń (24.10.2026) — start jak ich dział.
  'CS_LEAD',
  'ADMINISTRATION_LEAD',
];

/** Parametr adresu „pokaż pulpit ogólny” — `/?widok=ogolny`. */
export const GENERAL_VIEW_PARAM = 'widok';
export const GENERAL_VIEW_VALUE = 'ogolny';

export type StartScreen = 'planner' | 'intake' | 'general';

/**
 * Który pulpit jest ekranem startowym (makieta back office v2, A4,
 * 20.10.2026: pulpit zależny od roli). Planista FTL — pulpit planisty (za
 * przełącznikiem `plannerDashboard`); pozostali — pulpit ogólny jak dotąd,
 * **także planista główny (Team Leader)**: decyzja użytkownika 20.10.2026 —
 * pulpit Team Leadera otwiera z menu, jak administratorzy. `?widok=ogolny`
 * zawsze pokazuje pulpit ogólny.
 *
 * CS i Administracja (makieta back office v2, fala B, 21.10.2026: „Pulpit CS”,
 * „Pulpit Administracji” jako ekrany startowe działów) — pulpit przyjęcia
 * zleceń, za przełącznikiem `intakeDepartments`.
 */
export function startScreenFor(
  role: string | null | undefined,
  features: { planner: boolean; intake?: boolean },
  general: boolean,
): StartScreen {
  if (general) return 'general';
  if (role === ROLE_PLANNER_FTL && features.planner) return 'planner';
  if (role && INTAKE_START_ROLES.includes(role) && features.intake) return 'intake';
  return 'general';
}

/** Ekran startowy `<Admin dashboard>` — patrz `startScreenFor`. */
export const RoleDashboard = () => {
  const { permissions: role, isPending } = usePermissions<string>();
  const planner = useFeatureState(FEATURE_PLANNER_DASHBOARD);
  const intake = useFeatureState(FEATURE_INTAKE_DEPARTMENTS);
  // Bez roli albo przełącznika — czekamy, zamiast mignąć pulpitem ogólnym.
  if (isPending || planner === undefined || intake === undefined) {
    return <FeaturesLoading />;
  }
  const general = hashQueryParams().get(GENERAL_VIEW_PARAM) === GENERAL_VIEW_VALUE;
  const screen = startScreenFor(role, { planner, intake }, general);
  if (screen === 'planner') return <PlannerDashboard />;
  if (screen === 'intake') return <IntakeDashboard />;
  return <Dashboard />;
};
